import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'

import { useToast } from '@hapana/shared/web/components/shared/toast/toast.hooks'
import {
  listAvailableCorporateUserIntegrations,
  listCorporateCustomIntegrations,
  setCorporateUserCustomIntegration,
  getCorporateBySubdomainQuery,
} from '_services/corporate-integrations.service'
import { TOAST_PROPS_OPTIONS } from '@hapana/shared/web/components/shared/toast/toast.constants'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import { useErrorHandler } from '@hapana/shared/web/hooks/errors.hooks'
import { GENERIC_ERROR_KEY } from '@hapana/shared/constants/errors.constants'
import { useFeatureTrackerOptional } from '@hapana/shared/web/hooks'
import useAuthStore from '_stores/auth.store'

export const useAvailableCorporateUserIntegrations = () => {
  return useQuery(
    [listAvailableCorporateUserIntegrations.key],
    listAvailableCorporateUserIntegrations.query
  )
}

export const useCorporateCustomIntegrations = (corporateId) => {
  return useQuery(
    [listCorporateCustomIntegrations, { corporateId }],
    listCorporateCustomIntegrations.query,
    {
      enabled: !!corporateId,
    }
  )
}

export const useGetCorporateBySubdomainQuery = (subdomain) => {
  const { onError } = useErrorHandler(GENERIC_ERROR_KEY)
  const { user, idToken, headersReady } = useAuthStore()

  return useQuery(
    [getCorporateBySubdomainQuery, { subdomain }],
    getCorporateBySubdomainQuery.query,
    {
      enabled: !!subdomain && !!user && !!idToken && headersReady,
      onError,
    }
  )
}

export const useSetCorporateUserCustomIntegration = (options) => {
  const { openToast } = useToast()
  const { t } = useTranslation('core')
  const { closeDialog } = useDialog()
  const queryClient = useQueryClient()
  const featureTracker = useFeatureTrackerOptional('settings.integrations')

  const onSuccess = (data, variables) => {
    openToast({
      title: t('controlCenter.integrations.modals.editIntegration.successfulUpdate'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
    })

    // Track audit event
    if (featureTracker) {
      const { onSuccess: auditOnSuccess } = featureTracker.createMutationCallbacks({
        changeType: 'update',
        resourceId: variables?.integrationId,
        settingName: `integration.custom.${variables?.integrationId}`,
        settingDisplayName: 'Corporate Custom Integration',
        getOldValue: () => {
          const cached = queryClient.getQueryData([{ key: listCorporateCustomIntegrations.key }])
          const prev = cached?.corporate?.thirdPartyIntegrations?.find(
            (i) => i.integrationId === variables?.integrationId
          )
          return prev ? JSON.stringify(prev.keys) : undefined
        },
        getNewValue: () => JSON.stringify(variables?.keys),
        getSummary: () =>
          `Corporate custom integration ${variables?.integrationId ?? ''} settings updated`,
      })
      auditOnSuccess(data)
    }

    options?.onSuccess?.(data)
    closeDialog()
    queryClient.invalidateQueries([{ key: listCorporateCustomIntegrations.key }])
  }
  const onError = (error) => {
    // Track audit error
    if (featureTracker) {
      const { onError: auditOnError } = featureTracker.createMutationCallbacks({
        changeType: 'update',
        settingName: 'integration.custom',
        settingDisplayName: 'Corporate Custom Integration',
        getSummary: () => 'Failed to update corporate custom integration settings',
      })
      auditOnError(error)
    }

    openToast({
      title: t('controlCenter.integrations.modals.editIntegration.unsuccessfulUpdate'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
    })
    options?.onError?.()
  }

  return useMutation(setCorporateUserCustomIntegration, {
    onSuccess,
    onError,
    ...options,
  })
}
